import { useEffect, type JSX } from 'react';
import * as Icons16 from 'dg-web-shared/ui/icons/Icons16.tsx';
import { IconButton24pxType } from '../buttons/IconButton.tsx';
import {
    ActionButton,
    ActionButtons,
    ActionButtonsContainer,
    BelowContent,
    Content,
    useHoverState,
    Label,
    LabeledElement,
} from './Internal.tsx';
import { LabeledTextStatus, LabeledTextStatusIcon } from './LabeledText.tsx';

interface Props {
    label: React.ReactNode;
    rightLabelText?: string;
    selection: React.ReactNode;
    onClear?: (() => void) | null;
    status?: LabeledTextStatus;
    errorText?: React.ReactNode;
    focused: boolean;
    onClick?: () => void;
    disabled?: boolean;
    toolTip?: React.ReactNode;
    toolTipRight?: boolean;
}

export function SingleSelection(props: Props) {
    const { actionHovered, setActionHovered, itemHovered, setItemHovered } =
        useHoverState();

    // Was UNSAFE_componentWillReceiveProps: drop the action hover once the
    // clear button goes away, so it cannot come back lit if it reappears.
    useEffect(() => {
        if (!props.onClear && actionHovered) {
            setActionHovered(false);
        }
    }, [props.onClear, actionHovered, setActionHovered]);

    function renderActionButton(): JSX.Element | null {
        return props.onClear ? (
            <ActionButtonsContainer>
                <ActionButton
                    type={IconButton24pxType.clear}
                    onMouseOver={() => setActionHovered(true)}
                    onMouseOut={() => setActionHovered(false)}
                    onClick={props.onClear}
                />
            </ActionButtonsContainer>
        ) : null;
    }

    function isHovered(): boolean {
        return actionHovered || itemHovered || false;
    }

    function isDisabled(): boolean {
        return !!props.disabled;
    }

    return (
        <LabeledElement
            onClick={isDisabled() ? undefined : props.onClick}
            onMouseOver={() => setItemHovered(true)}
            onMouseOut={() => setItemHovered(false)}
            actionButtons={
                props.onClear ? ActionButtons.one : ActionButtons.none
            }
        >
            <Label
                disabled={isDisabled()}
                label={props.label}
                rightText={props.rightLabelText}
                hovered={isHovered()}
                focused={props.focused}
                toolTip={props.toolTip}
                toolTipRight={props.toolTipRight}
            />
            <LabeledTextStatusIcon status={props.status} />
            <Content
                icon={
                    !actionHovered && !props.disabled
                        ? Icons16.chevronRight
                        : null
                }
                focused={props.focused}
                empty={!props.selection}
                userSelectable={false}
                hovered={isHovered()}
                disabled={isDisabled()}
            >
                {!props.selection ? '—' : props.selection}
                {!props.disabled && renderActionButton()}
            </Content>
            <BelowContent
                underline={true}
                focused={props.focused}
                hovered={isHovered()}
                errorText={props.errorText}
            />
        </LabeledElement>
    );
}
